import React, { useCallback, useEffect, useState } from 'react';
import {
  ActionIcon, Anchor, Autocomplete, Badge, Button, Checkbox, FileInput, Group, Loader, Modal,
  MultiSelect, Pagination, Select, Stack, Table, Text, TextInput,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useAppContext } from '../contexts/AppContext';
import { ROLLEN as FUNCTIEROLLEN } from '../utils/rollen';
import { CHANGELOG } from '../changelog';
import { weergavenaam } from '../utils/gebruikers';

// Op- en aanmerkingen: soorten en statussen.
const FEEDBACK_SOORTEN = [
  { value: 'issue', label: 'Issue (bug)' },
  { value: 'wens', label: 'Wens (feature)' },
  { value: 'verbetering', label: 'Verbetering' },
];
const FEEDBACK_STATUS = {
  open: { label: 'Open', kleur: 'yellow' },
  'in-behandeling': { label: 'In behandeling', kleur: 'blue' },
  afgehandeld: { label: 'Afgehandeld', kleur: 'green' },
};
const SOORT_KLEUR = { issue: 'red', wens: 'grape', verbetering: 'teal' };

// Leesbare labels voor de audit-acties (recente wijzigingen).
const ACTIE_LABEL = {
  'project-gewijzigd': 'Station/locatie gewijzigd',
  'versie-gekozen': 'Locatie gekozen',
  'versie-akkoord': 'Locatie-status gewijzigd',
  'sessie-gestart': 'Belangentafel gestart',
  'sessie-gestopt': 'Belangentafel gestopt',
  'sessie-afgerond': 'Belangentafel afgerond',
  'sessie-verwijderd': 'Belangentafel verwijderd',
  'rapportage-gegenereerd': 'Rapportage gegenereerd',
  'rapportage-definitief': 'Rapportage definitief gemaakt',
  'excel-import': 'Excel geïmporteerd',
  'gebruiker-gewijzigd': 'Gebruiker gewijzigd',
  'gebruiker-verwijderd': 'Gebruiker verwijderd',
  'aannemer-toegevoegd': 'Aannemercombinatie toegevoegd',
  'aannemer-gewijzigd': 'Aannemercombinatie gewijzigd',
  'aannemer-verwijderd': 'Aannemercombinatie verwijderd',
};

// Gebruikersbeheer — alleen voor Stedin-gebruikers (beheerders).
// Titels per beheersectie (drie losse menu-items in het gebruikersmenu).
const SECTIE_TITEL = {
  gebruikers: 'Gebruikersbeheer',
  import: 'Data-import',
  audit: 'Recente wijzigingen',
  changelog: 'Changelog',
  feedback: 'Op- en aanmerkingen',
};

// Wordt geopend vanuit het gebruikersmenu (UserMenu). sectie bepaalt welk deel
// getoond wordt: 'gebruikers' | 'import' | 'audit' (null = dicht).
export default function AdminPanel({ sectie, onClose }) {
  const opened = !!sectie;
  const { user, fetchWithAuth, addToast, zoekEnGaNaar } = useAppContext();
  const [users, setUsers] = useState([]);
  const [regios, setRegios] = useState([]);   // [{regio, gemeenten:[{code,naam}]}]
  const [laden, setLaden] = useState(false);
  const [importBestand, setImportBestand] = useState(null);
  const [importBezig, setImportBezig] = useState(false);
  const [importResultaat, setImportResultaat] = useState('');
  const [audit, setAudit] = useState([]);
  const [auditLaden, setAuditLaden] = useState(false);
  const [auditPagina, setAuditPagina] = useState(1);
  const [feedback, setFeedback] = useState([]);
  const [nieuwFb, setNieuwFb] = useState({ soort: 'issue', titel: '', omschrijving: '' });
  const smalScherm = useMediaQuery('(max-width: 768px)');
  const AUDIT_PER_PAGINA = 12;

  const verwijderGebruiker = async (username) => {
    if (!window.confirm(`Gebruiker ${username} definitief verwijderen?`)) return;
    try {
      const res = await fetchWithAuth(`/api/admin/users/${encodeURIComponent(username)}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.success) {
        addToast && addToast(`${username} verwijderd`, 'success');
        laadUsers();
      } else {
        addToast && addToast(data.error || 'Verwijderen mislukt', 'error');
      }
    } catch (e) {
      addToast && addToast('Verwijderen mislukt', 'error');
    }
  };

  const voerImportUit = async () => {
    if (!importBestand) return;
    setImportBezig(true);
    setImportResultaat('');
    try {
      const form = new FormData();
      form.append('bestand', importBestand);
      const res = await fetchWithAuth('/api/admin/import/excel', { method: 'POST', body: form });
      const data = await res.json();
      if (data.success) {
        setImportResultaat(`Import gelukt: ${data.buurten} buurten en ${data.projecten} projecten bijgewerkt.`);
        setImportBestand(null);
      } else {
        setImportResultaat(`Import mislukt: ${data.error}`);
      }
    } catch (e) {
      setImportResultaat('Import mislukt: netwerkfout');
    } finally {
      setImportBezig(false);
    }
  };

  const isBeheerder = !!user?.admin;

  const laadUsers = useCallback(async () => {
    setLaden(true);
    try {
      const res = await fetchWithAuth('/api/admin/users');
      const data = await res.json();
      if (data.success) setUsers(data.users);
      else addToast && addToast(data.error || 'Gebruikers laden mislukt', 'error');
    } catch (e) {
      addToast && addToast('Gebruikers laden mislukt', 'error');
    } finally {
      setLaden(false);
    }
  }, [fetchWithAuth, addToast]);

  const laadRegios = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/admin/gemeenten');
      const data = await res.json();
      if (data.success) setRegios(data.regios || []);
    } catch (e) { /* stil: multiselect blijft leeg */ }
  }, [fetchWithAuth]);

  const laadAudit = useCallback(async () => {
    setAuditLaden(true);
    try {
      const res = await fetchWithAuth('/api/admin/audit?limiet=200');
      const data = await res.json();
      if (data.success) { setAudit(data.audit); setAuditPagina(1); }
    } catch (e) { /* stil: overzicht blijft leeg */ } finally {
      setAuditLaden(false);
    }
  }, [fetchWithAuth]);

  const laadFeedback = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/admin/feedback');
      const data = await res.json();
      if (data.success) setFeedback(data.feedback);
    } catch (e) { /* stil: lijst blijft leeg */ }
  }, [fetchWithAuth]);

  const voegFeedbackToe = async () => {
    if (!nieuwFb.titel.trim()) return;
    try {
      const res = await fetchWithAuth('/api/admin/feedback', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nieuwFb),
      });
      const data = await res.json();
      if (data.success) { setNieuwFb({ soort: 'issue', titel: '', omschrijving: '' }); laadFeedback(); }
      else addToast && addToast(data.error || 'Toevoegen mislukt', 'error');
    } catch (e) { addToast && addToast('Toevoegen mislukt', 'error'); }
  };

  const wijzigFeedback = async (id, velden) => {
    setFeedback(prev => prev.map(f => (f.id === id ? { ...f, ...velden } : f)));
    try {
      const res = await fetchWithAuth(`/api/admin/feedback/${id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden),
      });
      if (!(await res.json()).success) throw new Error();
    } catch (e) { addToast && addToast('Opslaan mislukt', 'error'); laadFeedback(); }
  };

  const verwijderFeedback = async (id) => {
    if (!window.confirm('Dit punt verwijderen?')) return;
    try {
      const res = await fetchWithAuth(`/api/admin/feedback/${id}`, { method: 'DELETE' });
      if ((await res.json()).success) laadFeedback();
    } catch (e) { addToast && addToast('Verwijderen mislukt', 'error'); }
  };

  useEffect(() => {
    if (sectie === 'gebruikers') { laadUsers(); laadRegios(); }
    if (sectie === 'audit') laadAudit();
    if (sectie === 'feedback') laadFeedback();
  }, [sectie, laadUsers, laadRegios, laadAudit, laadFeedback]);

  if (!isBeheerder) return null;

  // Grouped data voor de gemeente-multiselect; per regio een "Hele regio"-item
  // dat bij selectie alle gemeenten van die regio toevoegt.
  const REGIO_PREFIX = '__regio__';
  const gemeenteData = regios.map(r => ({
    group: r.regio,
    items: [
      { value: REGIO_PREFIX + r.regio, label: `➕ Hele regio ${r.regio}` },
      ...r.gemeenten.map(g => ({ value: g.code, label: g.naam })),
    ],
  }));

  const wijzigGemeenten = (username, waarden) => {
    // "Hele regio"-tokens uitklappen naar alle gemeentecodes van die regio.
    const codes = new Set();
    waarden.forEach(v => {
      if (v.startsWith(REGIO_PREFIX)) {
        const regio = v.slice(REGIO_PREFIX.length);
        (regios.find(r => r.regio === regio)?.gemeenten || []).forEach(g => codes.add(g.code));
      } else codes.add(v);
    });
    const lijst = [...codes];
    setUsers(prev => prev.map(u => (u.username === username ? { ...u, gemeenten: lijst } : u)));
    fetchWithAuth(`/api/admin/users/${encodeURIComponent(username)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gemeenten: lijst }),
    }).then(r => r.json()).then(d => { if (!d.success) throw new Error(); })
      .catch(() => { addToast && addToast('Koppeling opslaan mislukt', 'error'); laadUsers(); });
  };

  const wijzig = async (username, veld, waarde) => {
    setUsers(prev => prev.map(u => (u.username === username ? { ...u, [veld]: waarde } : u)));
    try {
      const res = await fetchWithAuth(`/api/admin/users/${encodeURIComponent(username)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [veld]: waarde }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
    } catch (e) {
      addToast && addToast('Opslaan mislukt', 'error');
      laadUsers();
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={SECTIE_TITEL[sectie] || 'Beheer'}
      size={sectie === 'gebruikers' ? '95%' : sectie === 'audit' ? '70%' : 'xl'}
      fullScreen={smalScherm} transitionProps={{ duration: 0 }}>
      {sectie === 'gebruikers' && (laden ? (
        <Group justify="center" p="md"><Loader size="sm" /></Group>
      ) : (
        <Table.ScrollContainer minWidth={1100}>
          <Table striped highlightOnHover verticalSpacing="xs" fontSize="sm" horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Gebruiker</Table.Th>
                <Table.Th>Voornaam</Table.Th>
                <Table.Th>Achternaam</Table.Th>
                <Table.Th>Regio / gemeenten</Table.Th>
                <Table.Th>Functierol</Table.Th>
                <Table.Th ta="center">Goedgekeurd</Table.Th>
                <Table.Th ta="center">Admin</Table.Th>
                <Table.Th ta="center"></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map(u => (
                <Table.Tr key={u.username}>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm">{weergavenaam(u)}</Text>
                      <Text size="xs" c="dimmed">{u.email}</Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs" placeholder="Voornaam" style={{ minWidth: 110 }}
                      value={u.voornaam || ''}
                      onChange={e => { const w = e.currentTarget.value; setUsers(prev => prev.map(x => x.username === u.username ? { ...x, voornaam: w } : x)); }}
                      onBlur={e => wijzig(u.username, 'voornaam', e.currentTarget.value)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs" placeholder="Achternaam" style={{ minWidth: 120 }}
                      value={u.achternaam || ''}
                      onChange={e => { const w = e.currentTarget.value; setUsers(prev => prev.map(x => x.username === u.username ? { ...x, achternaam: w } : x)); }}
                      onBlur={e => wijzig(u.username, 'achternaam', e.currentTarget.value)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <MultiSelect
                      size="xs" searchable clearable hidePickedOptions
                      placeholder={(u.gemeenten || []).length ? '' : 'Kies regio/gemeenten…'}
                      style={{ minWidth: 260, maxWidth: 340 }}
                      data={gemeenteData}
                      value={u.gemeenten || []}
                      onChange={waarden => wijzigGemeenten(u.username, waarden)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Autocomplete
                      size="xs" style={{ minWidth: 200 }}
                      value={u.functierol || ''}
                      data={FUNCTIEROLLEN}
                      onChange={waarde => setUsers(prev => prev.map(x => x.username === u.username ? { ...x, functierol: waarde } : x))}
                      onBlur={e => wijzig(u.username, 'functierol', e.currentTarget.value)}
                    />
                  </Table.Td>
                  <Table.Td ta="center">
                    <Checkbox
                      checked={!!u.approved}
                      onChange={e => wijzig(u.username, 'approved', e.currentTarget.checked)}
                    />
                  </Table.Td>
                  <Table.Td ta="center">
                    <Checkbox
                      color="orange"
                      checked={!!u.admin}
                      disabled={u.username === user?.username}
                      title={u.username === user?.username ? 'Je kunt je eigen adminrol niet wijzigen' : undefined}
                      onChange={e => wijzig(u.username, 'admin', e.currentTarget.checked)}
                    />
                  </Table.Td>
                  <Table.Td ta="center">
                    {u.username !== user?.username && (
                      <ActionIcon
                        variant="subtle" color="red" size="sm"
                        aria-label={`Verwijder ${u.username}`}
                        onClick={() => verwijderGebruiker(u.username)}
                      >
                        ✕
                      </ActionIcon>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        ))}

      {sectie === 'import' && (
        <>
          <Group align="flex-end" gap="xs">
            <FileInput
              label="Buurten/projecten-Excel (.xlsx/.xlsm)"
              placeholder="Kies bestand..."
              accept=".xlsx,.xlsm"
              value={importBestand}
              onChange={setImportBestand}
              style={{ flex: 1 }}
              size="xs"
            />
            <Button size="xs" onClick={voerImportUit} loading={importBezig}
              disabled={!importBestand}>
              Importeren
            </Button>
          </Group>
          {importResultaat && <Text size="xs" c="dimmed" mt={4}>{importResultaat}</Text>}
        </>
      )}

      {sectie === 'audit' && (auditLaden ? (
        <Group justify="center" p="sm"><Loader size="sm" /></Group>
      ) : (
        <Stack gap="sm">
          <Table striped verticalSpacing={6} fontSize="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Wanneer</Table.Th>
                <Table.Th>Wie</Table.Th>
                <Table.Th>Wat</Table.Th>
                <Table.Th>Betreft</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {audit
                .slice((auditPagina - 1) * AUDIT_PER_PAGINA, auditPagina * AUDIT_PER_PAGINA)
                .map(a => (
                  <Table.Tr key={a.id}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      {a.tijdstip ? new Date(a.tijdstip).toLocaleString('nl-NL') : ''}
                    </Table.Td>
                    <Table.Td>{weergavenaam(a, a.username)}</Table.Td>
                    <Table.Td>{ACTIE_LABEL[a.actie] || a.actie}</Table.Td>
                    <Table.Td>
                      {a.doel_voornaam || a.doel_achternaam ? (
                        // Gebruikersacties: geen kaartdoel, dus gewone tekst.
                        weergavenaam({ voornaam: a.doel_voornaam, achternaam: a.doel_achternaam })
                      ) : a.doel ? (
                        // Klik = zoeken + inzoomen, alsof in de zoekbalk ingevuld.
                        <Anchor
                          component="button" type="button" size="sm"
                          title="Zoek en zoom hiernaartoe"
                          onClick={() => { onClose(); zoekEnGaNaar(a.doel); }}
                        >
                          {a.doel}
                        </Anchor>
                      ) : ''}
                    </Table.Td>
                  </Table.Tr>
                ))}
              {audit.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="xs" c="dimmed">Nog geen wijzigingen geregistreerd.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          {audit.length > AUDIT_PER_PAGINA && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {(auditPagina - 1) * AUDIT_PER_PAGINA + 1}–
                {Math.min(auditPagina * AUDIT_PER_PAGINA, audit.length)} van {audit.length}
              </Text>
              <Pagination size="sm" value={auditPagina} onChange={setAuditPagina}
                total={Math.ceil(audit.length / AUDIT_PER_PAGINA)} />
            </Group>
          )}
        </Stack>
      ))}

      {sectie === 'changelog' && (
        <Stack gap="md">
          {CHANGELOG.map(rel => (
            <div key={rel.datum}>
              <Text fw={600} size="sm">
                {new Date(rel.datum).toLocaleDateString('nl-NL', { dateStyle: 'long' })}
              </Text>
              <Stack gap={2} mt={2}>
                {rel.wijzigingen.map((w, i) => (
                  <Text key={i} size="sm" style={{ paddingLeft: 12, textIndent: -10 }}>• {w}</Text>
                ))}
              </Stack>
            </div>
          ))}
        </Stack>
      )}

      {sectie === 'feedback' && (
        <Stack gap="sm">
          <Group align="flex-end" gap="xs">
            <Select size="xs" label="Soort" data={FEEDBACK_SOORTEN} style={{ width: 150 }}
              value={nieuwFb.soort} onChange={v => setNieuwFb(n => ({ ...n, soort: v }))} />
            <TextInput size="xs" label="Titel" placeholder="Korte omschrijving" style={{ flex: 1 }}
              value={nieuwFb.titel}
              onChange={e => { const w = e.currentTarget.value; setNieuwFb(n => ({ ...n, titel: w })); }} />
          </Group>
          <Group align="flex-end" gap="xs">
            <TextInput size="xs" label="Toelichting (optioneel)" placeholder="Details…" style={{ flex: 1 }}
              value={nieuwFb.omschrijving}
              onChange={e => { const w = e.currentTarget.value; setNieuwFb(n => ({ ...n, omschrijving: w })); }} />
            <Button size="xs" onClick={voegFeedbackToe} disabled={!nieuwFb.titel.trim()}>Toevoegen</Button>
          </Group>
          {feedback.length === 0 && (
            <Text size="xs" c="dimmed">Nog geen op- of aanmerkingen vastgelegd.</Text>
          )}
          {feedback.map(f => (
            <Group key={f.id} justify="space-between" wrap="nowrap" align="flex-start"
              style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', paddingBottom: 6 }}>
              <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                <Group gap={6} wrap="nowrap">
                  <Badge size="xs" color={SOORT_KLEUR[f.soort] || 'gray'} variant="light">
                    {(FEEDBACK_SOORTEN.find(s => s.value === f.soort) || {}).label || f.soort}
                  </Badge>
                  <Text size="sm" fw={500}>{f.titel}</Text>
                </Group>
                {f.omschrijving && <Text size="xs" c="dimmed">{f.omschrijving}</Text>}
                <Text size="xs" c="dimmed">
                  {weergavenaam(f, f.aangemaakt_door)}{f.aangemaakt_op ? ` · ${new Date(f.aangemaakt_op).toLocaleDateString('nl-NL')}` : ''}
                </Text>
              </Stack>
              <Group gap={6} wrap="nowrap">
                <Select size="xs" style={{ width: 140 }} value={f.status}
                  data={Object.entries(FEEDBACK_STATUS).map(([v, o]) => ({ value: v, label: o.label }))}
                  onChange={v => wijzigFeedback(f.id, { status: v })}
                  styles={{ input: { color: `var(--mantine-color-${FEEDBACK_STATUS[f.status]?.kleur || 'gray'}-7)` } }} />
                <ActionIcon variant="subtle" color="red" size="sm"
                  aria-label={`Verwijder ${f.titel}`} onClick={() => verwijderFeedback(f.id)}>
                  ✕
                </ActionIcon>
              </Group>
            </Group>
          ))}
        </Stack>
      )}
    </Modal>
  );
}
